import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useSyncedState} from '@github-ui/use-synced-state'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import debounce from 'lodash-es/debounce'
import {memo, useCallback, useDeferredValue, useEffect, useImperativeHandle, useMemo, useRef} from 'react'

import {SearchBarUI} from '../../api/stats/contracts'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useIsFetchingVariable} from '../../hooks/use-is-fetching-variable'
import {useViews} from '../../hooks/use-views'
import {usePaginatedTotalCount} from '../../state-providers/memex-items/queries/use-paginated-total-count'
import styles from './base-project-view-filter-input.module.css'
import {useSearch} from './search-context'
import {TokenizedFilterInput, type TokenizedFilterInputProps} from './tokenized-filter-input'
import {useHandleFilterBarShortcut} from './use-handle-filter-bar-shortcut'

type BaseProjectViewFilterInputProps = {
  /** A callback invoked when a keydown occurs in the input **/
  onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  /** A callback to dispatch a navigation event when the input is focused **/
  dispatchInputFocusEvent?: () => void
  /** Whether the input is currently focused  **/
  isFocused?: boolean
}

export const BaseProjectViewFilterInput = memo(function BaseProjectViewFilterInput({
  isFocused,
  ...props
}: BaseProjectViewFilterInputProps) {
  const {searchInputRef} = useSearch()

  useEffect(() => {
    if (isFocused) searchInputRef.current?.focus()
  }, [isFocused, searchInputRef])

  return <BaseProjectInputWithActions {...props} />
})

/** The primary project filter input, which includes save and discard functionality **/
const BaseProjectInputWithActions = memo(function BaseProjectInputWithActions(
  props: Omit<BaseProjectViewFilterInputProps, 'isFocused'>,
) {
  const {currentView, isViewStateDirty, saveCurrentViewState, resetViewState} = useViews()
  const {hasWritePermissions} = ViewerPrivileges()

  const currentViewNumber = currentView?.number
  const displayButtonEnabled = useFeatureFlag('memex_display_button_config_menu')

  // When display button is enabled, only show filter input buttons when the filter itself is dirty
  // When disabled, show buttons for any view state change (original behavior)
  const isDirtyForFilterButtons = displayButtonEnabled ? currentView?.isFilterDirty ?? false : isViewStateDirty

  const handleSaveView = useCallback(async () => {
    if (currentViewNumber === undefined) return
    // When display button is enabled, only save the filter
    // When disabled, save everything (original behavior)
    const saveScope = displayButtonEnabled ? 'filter' : undefined
    await saveCurrentViewState(
      currentViewNumber,
      {
        ui: SearchBarUI,
      },
      saveScope,
    )
  }, [currentViewNumber, displayButtonEnabled, saveCurrentViewState])

  const handleResetChanges = useCallback(async () => {
    if (currentViewNumber === undefined) return
    // When display button is enabled, only reset the filter
    // When disabled, reset everything (original behavior)
    const resetScope = displayButtonEnabled ? 'filter' : undefined
    resetViewState(
      currentViewNumber,
      {
        ui: SearchBarUI,
      },
      resetScope,
    )
  }, [currentViewNumber, displayButtonEnabled, resetViewState])

  return (
    <ProjectInputWithSearchContext
      onResetChanges={isDirtyForFilterButtons ? handleResetChanges : undefined}
      onSaveChanges={!currentView?.isDeleted && isDirtyForFilterButtons ? handleSaveView : undefined}
      hideSaveButton={!hasWritePermissions}
      {...props}
    />
  )
})

type ProjectInputWithSearchContextProps = Partial<TokenizedFilterInputProps> &
  Pick<BaseProjectViewFilterInputProps, 'dispatchInputFocusEvent'> & {
    children?: React.ReactNode
  }

/** Suggestion wrapped filter input that uses search context for managing query state **/
export const ProjectInputWithSearchContext = memo(function ProjectInputWithSearchContext({
  dispatchInputFocusEvent,
  children,
  ...props
}: ProjectInputWithSearchContextProps) {
  const {currentView} = useViews()
  const {query, setQuery, searchInputRef, addSpaceToQuery} = useSearch()
  const searchRef = useRef<HTMLInputElement | null>(null)
  const debouncedSearch = useMemo(() => debounce(setQuery, 200), [setQuery])
  const {value: totalCountValue} = usePaginatedTotalCount()
  const isFetching = useIsFetchingVariable('q')
  const showFilterCount = query.length > 0 && !isFetching
  const filterCount = useDeferredValue(showFilterCount ? totalCountValue : null)

  const currentViewNumber = currentView?.number

  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(searchInputRef, () => searchRef.current)

  // LocalQuery is used for a better user experience
  // Because the search is debounced, using the actual query for setting value on TextInput will be perceived as laggy
  const [localQuery, setLocalQuery] = useSyncedState(query)

  const trackingAddSpaceToQuery = useTrackingRef(addSpaceToQuery)
  const handleFocus = useCallback(() => {
    if (dispatchInputFocusEvent) {
      dispatchInputFocusEvent()
    }
  }, [dispatchInputFocusEvent])

  const handleValueChange = useCallback(
    (value: string) => {
      if (currentViewNumber === undefined) return
      debouncedSearch.cancel()
      setLocalQuery(value)
      debouncedSearch(currentViewNumber, value)
    },
    [currentViewNumber, debouncedSearch, setLocalQuery],
  )

  const onChangeValue = useCallback(
    (value: string) => {
      handleValueChange(value)
    },
    [handleValueChange],
  )

  const onFilterBarShortcut = useCallback(() => {
    handleFocus()
    trackingAddSpaceToQuery.current()
  }, [handleFocus, trackingAddSpaceToQuery])

  useHandleFilterBarShortcut(onFilterBarShortcut)

  return (
    <div className={styles.Box}>
      <TokenizedFilterInput
        value={localQuery}
        onChangeValue={onChangeValue}
        inputRef={searchRef}
        filterCount={filterCount}
        {...props}
      >
        {children}
      </TokenizedFilterInput>
    </div>
  )
})

BaseProjectViewFilterInput.displayName = 'BaseProjectViewFilterInput'

try{ BaseProjectInputWithActions.displayName ||= 'BaseProjectInputWithActions' } catch {}
try{ ProjectInputWithSearchContext.displayName ||= 'ProjectInputWithSearchContext' } catch {}